import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CommonDatagrid } from '../../components/DataGrid';
import { ViewButton } from '../../components/Buttons/ViewButton';
import { RefreshButton } from '../../components/Buttons/RefreshButton';
import { ViewOrderDialog } from '../../components/Dialog/ViewOrderDialog';
import { OrdersService } from '../../api/services/ordersService';
import { IOrderSummary } from '../../types/api/response/ordersresponse';

const ORDER_TYPE_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'warning'> = {
  timeslot: 'primary',
  instant: 'warning',
  standard: 'default',
};

const ORDER_STATUS_COLOR: Record<
  string,
  'default' | 'warning' | 'info' | 'success' | 'error' | 'primary'
> = {
  COMPLETED: 'success',
  CANCELLED: 'error',
  ONWAY: 'info',
  ASSIGNED: 'primary',
  APPROVED: 'warning',
};

const formatDate = (val: string) => new Date(val).toLocaleString();

export const OrdersPage = () => {
  const [orders, setOrders] = useState<IOrderSummary[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrderSummary | null>(null);
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const load = async (p = page, s = pageSize, q = search) => {
    setLoading(true);
    try {
      const res = await OrdersService.getOrders(p, s, q || undefined);
      setOrders(res.content ?? []);
      setTotalElements(res.page?.totalElements ?? 0);
    } catch {
      // error handled silently — could dispatch notification if needed
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, pageSize, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0);
  };
  const handleSearch = (q: string) => {
    setSearch(q);
    setPage(0);
  };

  const onClickView = (order: IOrderSummary) => {
    setSelectedOrder(order);
    setIsOpenDialog(true);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'order_number',
        headerName: 'Order No',
        minWidth: 160,
        renderCell: (params: GridRenderCellParams) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.row.ds_order_number ?? params.row.order_number}
            </Typography>
            {params.row.ds_order_number && (
              <Typography variant="caption" color="text.secondary">
                {params.row.order_number}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'order_date',
        headerName: 'Date',
        minWidth: 160,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2">{formatDate(params.row.order_date)}</Typography>
        ),
      },
      {
        field: 'order_type',
        headerName: 'Type',
        minWidth: 110,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.row.order_type}
            color={ORDER_TYPE_COLOR[params.row.order_type] ?? 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'order_status',
        headerName: 'Status',
        minWidth: 130,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.row.order_status}
            color={ORDER_STATUS_COLOR[params.row.order_status] ?? 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'payment_status',
        headerName: 'Payment',
        minWidth: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Box>
            <Typography variant="body2">{params.row.payment_type}</Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.payment_status}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'customer_name',
        headerName: 'Customer',
        minWidth: 160,
        renderCell: (params: GridRenderCellParams) => (
          <Box>
            <Typography variant="body2">{params.row.customer_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.customer_phone}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'order_total',
        headerName: 'Total',
        minWidth: 100,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" fontWeight={600}>
            ${Number(params.row.order_total).toFixed(2)}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        minWidth: 80,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <ViewButton onClick={() => onClickView(params.row as IOrderSummary)} />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Orders
      </Typography>

      <CommonDatagrid
        data={orders}
        columns={columns}
        rowId="order_number"
        serverPagination
        page={page}
        pageSize={pageSize}
        rowCount={totalElements}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onChangeSearchKeyword={handleSearch}
        searchbarPlaceholder="Search by order number or DS order number..."
        headerActionButtons={<RefreshButton onClick={() => load(page, pageSize, search)} />}
      />

      {selectedOrder && (
        <ViewOrderDialog
          selectedOrder={selectedOrder}
          isOpenDialog={isOpenDialog}
          setIsOpenDialog={setIsOpenDialog}
        />
      )}
    </Box>
  );
};
